/**
 * Lead Download Dialog Component
 * Located: frontend/src/features/leads/components/LeadDownloadDialog.jsx
 */

import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Download,
  Close,
  Info,
  Security,
  Description,
  Image,
  VideoFile,
  AudioFile,
  PictureAsPdf,
} from "@mui/icons-material";

const LeadDownloadDialog = ({ open, onClose, lead, onDownload }) => {
  const [includeCustomerDetails, setIncludeCustomerDetails] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const handleDownload = useCallback(async () => {
    if (!lead) return;

    setIsDownloading(true);
    setDownloadError(null);

    try {
      await onDownload(lead._id, includeCustomerDetails);
      onClose();
    } catch (error) {
      console.error("Download failed:", error);
      setDownloadError(error.message || "Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }, [lead, includeCustomerDetails, onDownload, onClose]);

  const handleClose = useCallback(() => {
    if (!isDownloading) {
      setDownloadError(null);
      onClose();
    }
  }, [isDownloading, onClose]);

  const getFileName = () => {
    if (!lead) return "lead.zip";

    if (includeCustomerDetails && lead.name && lead.phone) {
      const cleanName = lead.name.replace(/[^a-zA-Z0-9]/g, "_");
      const cleanPhone = lead.phone.replace(/[^0-9]/g, "");
      return `${cleanName}_${cleanPhone}_${lead._id}.zip`;
    }
    return `lead_${lead._id}.zip`;
  };

  const getMediaCount = () => {
    if (!lead?.items) return 0;
    return lead.items.filter((item) => item.type !== "text").length;
  };

  const getTextItemsCount = () => {
    if (!lead?.items) return 0;
    return lead.items.filter((item) => item.type === "text").length;
  };

  const getMediaTypes = () => {
    if (!lead?.items) return [];
    const types = new Set();
    lead.items.forEach((item) => {
      if (item.type !== "text") {
        types.add(item.type);
      }
    });
    return Array.from(types);
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case "image":
        return <Image />;
      case "video":
        return <VideoFile />;
      case "audio":
        return <AudioFile />;
      case "document":
        return <Description />;
      default:
        return <Description />;
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Download color="primary" />
            <Typography variant="h6">Download Lead</Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={isDownloading}
            size="small"
            aria-label="Close dialog"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Lead Information */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Lead Information
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            <Chip label={`ID: ${lead._id}`} size="small" variant="outlined" />
            <Chip
              label={`Status: ${lead.status || "N/A"}`}
              size="small"
              color="primary"
            />
            <Chip
              label={`Source: ${lead.source || "N/A"}`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Privacy Settings */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <Security sx={{ mr: 1, verticalAlign: "middle" }} />
            Privacy Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={includeCustomerDetails}
                onChange={(e) => setIncludeCustomerDetails(e.target.checked)}
                disabled={isDownloading}
                inputProps={{
                  "aria-label": "Include customer details in download",
                }}
              />
            }
            label="Include Customer Details"
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {includeCustomerDetails ? (
              <>
                <strong>Customer details will be included:</strong> Name, phone
                number, email, and address will be visible in the PDF and
                filename.
              </>
            ) : (
              <>
                <strong>Customer details will be redacted:</strong> Personal
                information will be hidden for privacy protection.
              </>
            )}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Download Preview */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <Info sx={{ mr: 1, verticalAlign: "middle" }} />
            Download Preview
          </Typography>

          <Box bgcolor="grey.50" p={2} borderRadius={1} mb={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <strong>Filename:</strong> {getFileName()}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Contents:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <PictureAsPdf color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Lead Summary PDF"
                  secondary="Complete lead details and information"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Description />
                </ListItemIcon>
                <ListItemText
                  primary="Download Report"
                  secondary="Detailed log of the download process"
                />
              </ListItem>
              {getMediaCount() > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <Image />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Media Files (${getMediaCount()})`}
                    secondary={
                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                        {getMediaTypes().map((type) => (
                          <Chip
                            key={type}
                            icon={getMediaIcon(type)}
                            label={type}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    }
                  />
                </ListItem>
              )}
              {getTextItemsCount() > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <Description />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Text Items (${getTextItemsCount()})`}
                    secondary="Listed in the PDF summary"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Box>

        {/* Error Display */}
        {downloadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {downloadError}
          </Alert>
        )}

        {/* Download Progress */}
        {isDownloading && (
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              Preparing download package...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isDownloading}>
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={
            isDownloading ? <CircularProgress size={16} /> : <Download />
          }
          disabled={isDownloading}
        >
          {isDownloading ? "Downloading..." : "Download"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadDownloadDialog;
